import { useCallback, useEffect, useState } from 'react';

import { authService } from '@/services/authService';
import type { CurrentUser } from '@/types/auth.types';
import { parseRestError } from '@/utils/errorUtils';

/** Drives `ProfilePage`. `AuthContext.user` already holds this from login, but the profile
 * screen re-fetches from `/account/me` so it reflects changes made elsewhere in the same
 * session (e.g. enabling MFA), rather than the stale snapshot captured at login time. */
export function useProfile() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await authService.getCurrentUser();
      setUser(data);
    } catch (err) {
      setError(parseRestError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { user, isLoading, error, refetch: load };
}
